import type { CbStatus } from '../components/consolidated-business/status';

export type KpiMode = 'net' | 'quantity' | 'grossprofit';
export type SortOrder = 'ASC' | 'DESC';
export type { CbStatus };

export type ConsBusinessSortField =
    | 'customerName' | 'productName' | 'salesManagerName' | 'lastInvoice'
    | 'kpiYTD' | 'kpi12m' | 'kpiYearAvg' | 'avgCycle' | 'daysSince';

export interface ConsBusinessRow {
    CustomerName: string | null;
    ProductName: string | null;
    SalesManagerName: string | null;
    CompanyCode: string | null;
    BusinessUnitCode: string | null;
    SupplierDimName: string | null;
    CategoryName: string | null;
    CustomerID: string | null;
    LastInvoice: string | null;
    AvgCycle: number | null;
    DaysSince: number | null;
    KpiYTD: number | null;
    Kpi12m: number | null;
    KpiYearAvg: number | null;
    UpdateDate: string | null;
    /** The one exclusive state (migration 132). A group with no check row reads 'Open'. */
    Status: CbStatus;
    /** Free text, only ever set while 'InProgress'. */
    StatusNote: string | null;
    /** Derived from Status, kept for readers that still think in flags. */
    CheckedAny: number;
    LostAny: number;
    CheckedCount: number;
    NextReviewDueAt: string | null;
    HubspotUrl: string | null;
}

export interface ConsBusinessStats {
    totalGroups: number;
    overdueGroups: number;
    /** checked + inProgress + unchecked + lost === totalGroups, exactly. */
    checkedGroups: number;
    inProgressGroups: number;
    uncheckedGroups: number;
    lostGroups: number;
    kpiYTD: number;
    kpi12m: number;
    dataAsOf: string | null;
    /** Config default for the review reminder — carried here since the import panel is gone. */
    defaultReviewDays: number;
}

export interface ConsBusinessFilterOptions {
    companies: { code: string; name: string }[];
    businessUnits: string[];
    salesManagers: string[];
    /**
     * Names, not ids: supplier and subclass ids are company-prefixed, so an id list showed the
     * same supplier once per company. Optional for the same reason as `salesTeams`.
     */
    supplierNames?: string[];
    categoryNames?: string[];
    /**
     * Only teams that actually have combinations here — an active team with none is left out
     * rather than offered as a dead option. Optional so a browser on the previous bundle, which
     * gets a response without the key, renders an empty picker instead of crashing on .map.
     */
    salesTeams?: string[];
}

export interface ConsBusinessFilters {
    companyCode?: string;
    businessUnit?: string;
    salesManager?: string;
    /**
     * Team NAME, not id — the same contract /frame-order-tracking uses, so a team means the same
     * thing in both places. Resolved server-side through Customer.SalesManagerID.
     */
    salesTeam?: string;
    /** Supplier NAME — spans every company's id for that supplier (see supplierNames). */
    supplierName?: string;
    /** Subclass NAME, same contract as supplierName. */
    categoryName?: string;
    search?: string;
    historyMonths?: string;
    monthsSinceLastInvoice?: string;
    minSales?: string;
    onlyOverdue?: boolean;
    /** One of the four states, or absent for "all". Replaces the old onlyUnchecked flag. */
    status?: CbStatus;
    /**
     * Exclude written-off combinations. Orthogonal to `status` (which SELECTS one state) except
     * for the one combination that cannot mean anything — see reconcileFilters().
     */
    hideLost?: boolean;
}

/**
 * The one invariant the filter model has: `status: 'Lost'` and `hideLost` together can only ever
 * return nothing. Asking to SEE the write-offs is the more specific, more deliberate act — it
 * takes a click on the Lost tile or pill — so it wins and drops the checkbox.
 *
 * This lives here, not in the two components that own those controls, because the rule belongs to
 * the filter model: the stat tiles and the filter pills both write `status`, and a third writer
 * added later must not be able to reach the dead state either.
 */
export function reconcileFilters(f: ConsBusinessFilters): ConsBusinessFilters {
    if (f.status === 'Lost' && f.hideLost) return { ...f, hideLost: undefined };
    return f;
}

/**
 * The drawer's payload. No members[] since migration 132: the batch variants were the source
 * table's grain, never a business fact, and the drawer shows the combination's own figures.
 */
export interface ConsBusinessGroupDetail {
    customerId: string;
    productName: string;
    /** Display only — derived from the members, not part of the group key. */
    customerName: string | null;
    /** Display only — functionally dependent on customerId, not part of the group key. */
    salesManagerName: string | null;
    companyCode: string | null;
    businessUnitCode: string | null;
    supplierDimName: string | null;
    categoryName: string | null;
    lastInvoice: string | null;
    avgCycle: number | null;
    daysSince: number | null;
    kpiYTD: number | null;
    kpi12m: number | null;
    kpiYearAvg: number | null;
    quantityAvgYear: number | null;
    updateDate: string | null;
    status: CbStatus;
    statusNote: string | null;
    reviewDueAt: string | null;
    checkedAt: string | null;
    checkedBy: string | null;
    hubspotUrl: string | null;
    /** 0-3 HubSpot deal ids. Measured firm-wide: never more than three per combination. */
    deals: string[];
}

// ─── Phase 2: writes, history, import ───────────────────────────────────────

/**
 * The body of a status write. Everything but `status` is optional, and an entirely empty
 * reminder is legal for 'InProgress' (D3) — the server never substitutes the config default
 * there, so "in progress, no reminder" stays exactly that.
 */
export interface StatusSetPayload {
    status: CbStatus;
    statusNote?: string | null;
    reviewDays?: number;      // 30 | 90 | 180 | 365 | custom N
    reviewDueDate?: string;   // 'yyyy-mm-dd' (explicit date wins over reviewDays)
}

export interface StatusSetResult {
    status: CbStatus;
    previousStatus: CbStatus;
    changed: boolean;
    reviewDueDate: string | null;
    reviewDaysUsed: number | null;
    statusNote: string | null;
}

/**
 * The group write, as the detail drawer and the list need it. Injected so the drawer can be
 * reused unchanged by the Customer-360 Lost-Business tab, which posts to its own
 * customer-scoped endpoint rather than the consolidated-business one (different permission
 * gate — see api/.../customer-360/cons-business-write.ts).
 *
 * An injected object, not a `mode` string: adding a third host then means passing a third
 * implementation, not adding a third branch inside the drawer.
 */
export interface ConsBusinessWriteApi {
    setStatus(groupKey: string, payload: StatusSetPayload): Promise<StatusSetResult>;
}

// ─── Transaction drill-down (right-click a row) ─────────────────────────────

/**
 * One INVOICE behind a group, folded across all its batch variants server-side.
 *
 * `salesPriceEUR` and `materialPriceEUR` are derived, never stored (material cost = net sales
 * minus gross profit; COGS is not a column anywhere in the warehouse). Both keep the sign of the
 * underlying amounts: a credit memo has a negative quantity AND a negative amount, so the ratio
 * is already the correct positive unit price. Never Math.abs(). A (near) zero quantity has no
 * meaningful price and comes back null, which renders '–'.
 */
export interface ConsBusinessTransaction {
    invoiceNo: string;
    postingDate: string | null;
    /** Stock | Direct | Manual | EOS, or 'Mixed' when one invoice combines them (7 firm-wide). */
    sourceType: string | null;
    /** Sales Invoice | Sales Credit Memo | Manual Import | Manual Correction, or 'Mixed' (17). */
    documentType: string | null;
    /** null when this invoice itself mixes units (9 firm-wide) or carries none. */
    salesUnit: string | null;
    quantity: number | null;
    netSalesEUR: number | null;
    grossProfitEUR: number | null;
    /** Net sales minus gross profit. */
    materialCostEUR: number | null;
    /** Net sales / quantity. */
    salesPriceEUR: number | null;
    /** Material cost / quantity. */
    materialPriceEUR: number | null;
    gpPercent: number | null;
    /** Source rows folded into this invoice (6'144 of 123'373 are > 1). */
    lineCount: number;
}

export interface ConsBusinessTransactionsResponse {
    customerId: string;
    productName: string;
    /** The group's single sales unit, or null when it mixes units or records none. */
    salesUnit: string | null;
    /**
     * The group genuinely mixes sales units (83 groups firm-wide) — quantities and unit prices
     * are not comparable and must not be totalled. Deliberately NOT `salesUnit === null`: two
     * groups record no unit at all, and those do not mix anything.
     */
    mixedUnits: boolean;
    transactions: ConsBusinessTransaction[];
    totals: {
        invoiceCount: number;
        lineCount: number;
        quantity: number;
        netSalesEUR: number;
        grossProfitEUR: number;
        materialCostEUR: number;
        /** Weighted: total ÷ total, never the mean of the per-row means. */
        avgSalesPriceEUR: number | null;
        avgMaterialPriceEUR: number | null;
        gpPercent: number | null;
        /** The list hit the TOP cap — so these totals are partial too. */
        truncated: boolean;
    };
}

export interface ConsBusinessHistoryEvent {
    Id: number;
    ConsolidatedID: string | null;
    /** Checked|Unchecked|InProgress|MarkedLost|UnmarkedLost|ImportedFromLegacy|
     *  CollapsedToGroupGrain|OrphanPurged|AutoUnchecked */
    EventKind: string;
    Source: string;      // App|PowerApp|AutoSweep|Migration
    OldIsChecked: number | null;
    NewIsChecked: number | null;
    OldIsLost: number | null;
    NewIsLost: number | null;
    /** Present on everything written since migration 132. */
    OldStatus: string | null;
    NewStatus: string | null;
    ProductName: string | null;
    ReviewDueAt: string | null;
    ActorEmail: string | null;
    Note: string | null;
    CreatedAt: string;
}

export interface ConsBusinessConfig {
    defaultReviewDays: number;
    autoUncheckEnabled: boolean;
    legacyImportEnabled: boolean;
    maxDeleteRows: number;
    maxDeletePct: number;
    updatedAt: string | null;
    updatedBy: string | null;
}

/**
 * Comment arms on [Reporting].[ConsBusinessComment].
 *
 * 'Group'    → EntityRef is the 64-char hex of the group's GroupKeyHash, computed SERVER-SIDE
 *              from the groupKey. The client never sends an entityRef: a JS reimplementation
 *              of HASHBYTES would drift silently and split the thread in two.
 * 'Customer' → EntityRef is the CustomerID.
 */
export type ConsBusinessCommentArm = 'Group' | 'Customer';

/**
 * Sync state of the mirrored HubSpot note (migration 135). NULL/undefined = sync not
 * applicable: Customer-arm comments and comments that predate the feature.
 */
export type ConsBusinessCommentSyncStatus =
    'Synced' | 'NoDeal' | 'Disabled' | 'Failed' | 'DeleteFailed' | 'Deleted';

export interface ConsBusinessComment {
    Id: number;
    CompanyCode: string;
    EntityType: ConsBusinessCommentArm;
    EntityRef: string;
    CommentText: string;
    CreatedBy: string;
    CreatedAt: string;
    UpdatedBy: string | null;
    UpdatedAt: string | null;
    HubspotSyncStatus?: ConsBusinessCommentSyncStatus | null;
    HubspotNoteId?: string | null;
    HubspotEnv?: string | null;
    HubspotSyncedAt?: string | null;
    HubspotSyncError?: string | null;
    /** Deep link to the note on its first deal's timeline; only set while Synced. */
    HubspotNoteUrl?: string | null;
}

/**
 * groupKey used by the drawer and every write endpoint: base64url(JSON([customerId, productName])).
 *
 * ID grain since migration 123 (2026-07-28). The key used to carry the three group NAMES, which
 * merged customer accounts that happen to share a name. CustomerID is now the customer axis;
 * SalesManagerName dropped out of the key entirely because it is functionally dependent on it.
 *
 * NULLs are coerced to '' to mirror SQL's ISNULL(..., N''). An empty customerId is rejected by
 * decodeGroupKey on the server — loudly, rather than silently addressing the wrong group.
 */
export function buildGroupKey(row: Pick<ConsBusinessRow, 'CustomerID' | 'ProductName'>): string {
    const json = JSON.stringify([row.CustomerID ?? '', row.ProductName ?? '']);
    // base64url without padding
    return btoa(unescape(encodeURIComponent(json)))
        .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
